// @ts-check
// Cliente de servicio del flujo de intentos server-authoritative
// (BLK-01-DIAGRAMS, TSK-0176, DEC-063/DEC-064, capa services de
// docs/03-frontend.md §4.2/§9.1). Mismo patrón EXACTO que
// `autocorrection-client.js#autocorregirDiagrama`: usa exclusivamente
// `apiRequest` de `api-client.js` (nunca `fetch` directo), no toca el DOM, no
// importa nada de `features/**`/`components/**`/`pages/**`/`state/**`, valida
// argumentos de forma defensiva con `TypeError` síncrono antes de llamar a la
// red, y propaga `ApiError` sin capturarla -- la UI que invoque estas
// funciones (`autocorrection-panel.js`) decide cómo mostrar cada error.
//
// Consume dos endpoints nuevos (DEC-063/DEC-064, backend ya en producción):
//   - `POST /exercises/{ejercicio_id}/attempts/start`: crea (o, si ya hay uno
//     abierto para el par usuario/ejercicio, DEVUELVE idempotentemente,
//     DEC-064 punto 4) un `IntentoEjercicio`. SIN body: el `inicio` lo fija
//     SIEMPRE el servidor con su propio reloj (server-authoritative, SEC-R4)
//     -- esta función NUNCA envía un timestamp del cliente, ni lo aceptaría
//     si se le pasara (no hay parámetro para ello).
//   - `POST /exercises/{ejercicio_id}/attempts/{intento_id}/hint`: marca
//     `pista_solicitada = true` en el intento abierto y devuelve el texto de
//     la pista (campo `pista` del ejercicio, DEC-063 punto 2). Idempotente en
//     el servidor respecto a la penalización (pedirla dos veces no penaliza
//     dos veces, DEC-063 punto 3) -- esta capa no reimplementa esa garantía,
//     solo la consume.
//
// Errores propagados sin capturar (`ApiError`): 401 sin sesión
// ('unauthorized'), 403 sin CSRF válido ('forbidden'), 404 ejercicio/intento
// inexistente ('not_found'), 409 -- p.ej. pedir pista sobre un intento ya
// cerrado -- ('conflict'), 422 argumentos inválidos ('validation_error').

import { apiRequest } from './api-client.js';

/**
 * @typedef {{
 *   intentoId: string,
 *   ejercicioId: string,
 *   inicio: string,
 *   pistaSolicitada: boolean,
 *   practica: boolean,
 *   resultado: string,
 * }} IntentoIniciado
 */

/**
 * @typedef {{
 *   pista: string,
 *   pistaSolicitada: boolean,
 * }} PistaSolicitada
 */

/**
 * Mapea la respuesta cruda (snake_case) de `POST .../attempts/start` al tipo
 * interno de frontend (camelCase). Defensivo con `unknown`, mismo criterio
 * que `autocorrection-client.js#mapearResultadoAutocorreccion`.
 * @param {unknown} datos
 * @returns {IntentoIniciado}
 */
export function mapearIntentoIniciado(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    intentoId: typeof crudo.intento_id === 'string' ? crudo.intento_id : '',
    ejercicioId: typeof crudo.ejercicio_id === 'string' ? crudo.ejercicio_id : '',
    inicio: typeof crudo.inicio === 'string' ? crudo.inicio : '',
    pistaSolicitada: Boolean(crudo.pista_solicitada),
    resultado: typeof crudo.resultado === 'string' ? crudo.resultado : 'pendiente',
    // Intento de PRÁCTICA: el alumno repasa un ejercicio ya completado. No
    // cuenta ni otorga XP. Degrada a `false` si no viene (servidor anterior),
    // que es como se comportaba antes de existir.
    practica: Boolean(crudo.practica),
  };
}

/**
 * Mapea la respuesta cruda (snake_case) de `POST .../attempts/{id}/hint` al
 * tipo interno de frontend (camelCase).
 * @param {unknown} datos
 * @returns {PistaSolicitada}
 */
export function mapearPistaSolicitada(datos) {
  const crudo = /** @type {Record<string, unknown>} */ (datos ?? {});
  return {
    pista: typeof crudo.pista === 'string' ? crudo.pista : '',
    pistaSolicitada: Boolean(crudo.pista_solicitada),
  };
}

/**
 * Inicia (o recupera idempotentemente, DEC-064 punto 4) el intento abierto
 * del ejercicio `ejercicioId` para la persona usuaria actual. NO acepta ni
 * envía ningún timestamp del cliente: el servidor fija `inicio` con su propio
 * reloj (server-authoritative, DEC-063/SEC-R4).
 * @param {{
 *   ejercicioId: string,
 *   csrfToken: string,
 *   signal?: AbortSignal,
 *   practica?: boolean,
 * }} opciones
 *   `practica` pide abrirlo sobre un ejercicio YA completado, para repasar.
 * @returns {Promise<IntentoIniciado>}
 */
export async function iniciarIntento(opciones) {
  const { ejercicioId, csrfToken, signal } = opciones ?? /** @type {any} */ ({});
  if (typeof ejercicioId !== 'string' || ejercicioId.trim() === '') {
    throw new TypeError('iniciarIntento: "ejercicioId" debe ser un string no vacío');
  }
  if (typeof csrfToken !== 'string' || csrfToken === '') {
    throw new TypeError('iniciarIntento: "csrfToken" debe ser un string no vacío (token CSRF vigente)');
  }

  // `?practica=true` abre el intento sobre un ejercicio YA completado, para
  // repasarlo. Ese intento no cuenta ni otorga XP -- y quién es práctica lo
  // decide el SERVIDOR a partir del progreso, no este parámetro: pedirla
  // sobre un ejercicio no completado no cambia nada.
  const sufijo = opciones?.practica === true ? '?practica=true' : '';
  const datos = await apiRequest(`/exercises/${encodeURIComponent(ejercicioId)}/attempts/start${sufijo}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrfToken },
    signal,
  });
  return mapearIntentoIniciado(datos);
}

/**
 * Pide la pista del ejercicio `ejercicioId` para el intento abierto
 * `intentoId` (DEC-063 punto 2/3). La penalización (10% fijo del XP máximo,
 * una sola vez por intento) la calcula y aplica el servidor al cerrar el
 * intento en la autocorrección (DEC-065); esta función solo entrega el texto
 * de la pista tras confirmar la persona usuaria en la UI (la confirmación es
 * responsabilidad de `autocorrection-panel.js`, no de esta capa de servicio).
 * @param {{
 *   ejercicioId: string,
 *   intentoId: string,
 *   csrfToken: string,
 *   signal?: AbortSignal,
 * }} opciones
 * @returns {Promise<PistaSolicitada>}
 */
export async function pedirPista(opciones) {
  const { ejercicioId, intentoId, csrfToken, signal } = opciones ?? /** @type {any} */ ({});
  if (typeof ejercicioId !== 'string' || ejercicioId.trim() === '') {
    throw new TypeError('pedirPista: "ejercicioId" debe ser un string no vacío');
  }
  if (typeof intentoId !== 'string' || intentoId.trim() === '') {
    throw new TypeError('pedirPista: "intentoId" debe ser un string no vacío');
  }
  if (typeof csrfToken !== 'string' || csrfToken === '') {
    throw new TypeError('pedirPista: "csrfToken" debe ser un string no vacío (token CSRF vigente)');
  }

  const datos = await apiRequest(
    `/exercises/${encodeURIComponent(ejercicioId)}/attempts/${encodeURIComponent(intentoId)}/hint`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrfToken },
      signal,
    },
  );
  return mapearPistaSolicitada(datos);
}
